import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { wizardHistoryDepth } from './wizardHistory';

/**
 * Open state of the public quote wizard (QuoteWizardDialog), kept in the URL (`?cotizar=1`) so a reload keeps the user
 * inside the flow and the browser's back closes the sheet. Shared by the pages that open it.
 */
export function useWizardRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const open = searchParams.get('cotizar') === '1';

  const setOpen = (next: boolean) => {
    if (next) {
      // Pushed, so the browser back button closes the wizard
      const params = new URLSearchParams(searchParams);
      params.set('cotizar', '1');
      setSearchParams(params, { state: { wizardPushed: true } });
      return;
    }
    // The wizard stacks one entry per step on top of the one we pushed (wizardHistory): pop them all with it
    const stepEntries = wizardHistoryDepth();
    const params = new URLSearchParams(searchParams);
    params.delete('cotizar');
    // Pop the entry we pushed; when the wizard was opened by a deep link (e.g. from the login page),
    // there is nothing of ours to pop, so replace the URL instead
    if ((location.state as { wizardPushed?: boolean } | null)?.wizardPushed) {
      navigate(-(1 + stepEntries));
      return;
    }
    if (stepEntries > 0) {
      // Step entries are still stacked on the entry that opened the wizard: pop them, then replace that one
      window.addEventListener('popstate', () => setSearchParams(params, { replace: true }), { once: true });
      navigate(-stepEntries);
      return;
    }
    setSearchParams(params, { replace: true });
  };

  return { open, setOpen };
}
